import { AxleChip, ChipsInRows } from '@/components/displayFields/chips';
import { VerticalStack } from '@/components/utils/linearLayouts';
import { WeekdayTimeWindow, dayOfWeekAbbrToIndex } from '@/utils/datetimes/weekdays';
import { Avatar, Box } from '@mui/material';
import { format } from 'date-fns';

export const PatientTimeslotPreferenceChip = ({
    timeslotPreference,
    showDay = true,
}: {
    timeslotPreference: WeekdayTimeWindow;
    showDay?: boolean;
}) => {
    return (
        <AxleChip
            // the styling here is necessary to make the chip avatar a little bigger than usual to fit 3 letters
            // (e.g., "SUN", "MON") instead of the usual 2 for QHP initials
            sx={{
                '& .MuiChip-avatar': {
                    marginLeft: 0,
                },
            }}
            avatar={
                showDay ? (
                    <Avatar sx={{ minHeight: 32, minWidth: 32 }}>{timeslotPreference.day}</Avatar>
                ) : undefined
            }
            label={`${format(
                // The actual date doesn't matter, it's just useful to convert to a date object to use the format function.
                new Date(`1900-01-01T${timeslotPreference.startTime}`),
                'h:mm a',
            )} - ${format(new Date(`1900-01-01T${timeslotPreference.endTime}`), 'h:mm a')}`}
        />
    );
};
const sortByWeekday = (a: WeekdayTimeWindow, b: WeekdayTimeWindow): number => {
    // First, compare by the day of the week
    const dayDiff = dayOfWeekAbbrToIndex[a.day] - dayOfWeekAbbrToIndex[b.day];
    if (dayDiff !== 0) {
        return Number(dayDiff);
    }
    // If days are the same, compare by the start time (in ascending order)
    return a.startTime.localeCompare(b.startTime);
};

export const PatientTimeslotPreferenceChipsStack = ({
    patientTimeslotPreferences,
    showDay = true,
}: {
    patientTimeslotPreferences: WeekdayTimeWindow[];
    showDay?: boolean;
}) => {
    return (
        <VerticalStack spacing={1} width={'auto'}>
            {[...patientTimeslotPreferences]
                .sort(sortByWeekday)
                .map((timeslotPreference, index) => (
                    <Box key={index}>
                        <PatientTimeslotPreferenceChip
                            timeslotPreference={timeslotPreference}
                            showDay={showDay}
                        />
                    </Box>
                ))}
        </VerticalStack>
    );
};

export const PatientTimeslotPreferenceChips = ({
    patientTimeslotPreferences,
    showDay = true,
}: {
    patientTimeslotPreferences: WeekdayTimeWindow[];
    showDay?: boolean;
}) => {
    return (
        <ChipsInRows>
            {/* Make a copy of the patient's timeslot preferences so we can mutate the ordering. */}
            {[...patientTimeslotPreferences]
                .sort(sortByWeekday)
                .map((timeslotPreference, index) => (
                    <PatientTimeslotPreferenceChip
                        key={index}
                        timeslotPreference={timeslotPreference}
                        showDay={showDay}
                    />
                ))}
        </ChipsInRows>
    );
};
